import React from 'react'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { BulkUploadProgress, BulkUploadResult } from '../../../api/LogLeadTempCRUD'

interface BulkUploadProgressModalProps {
  show: boolean
  progress: BulkUploadProgress | null
  onClose: () => void
  dataResults: BulkUploadResult | null
  setDataResults: (dataResults: BulkUploadResult | null) => void
}

const BulkUploadProgressModal: React.FC<BulkUploadProgressModalProps> = ({
  show,
  progress,
  onClose,
  dataResults,
  setDataResults
}) => {
  const intl = useIntl()

  if (!progress) return null

  return (
    <Modal
      show={show}
      onHide={progress.isCompleted ? onClose : undefined}
      backdrop={progress.isCompleted ? true : 'static'}
      keyboard={progress.isCompleted}
      centered
      size="lg"
    >
      <Modal.Header closeButton={progress.isCompleted}>
        <Modal.Title>
          <i className="fas fa-upload me-2"></i>
          {progress.isCompleted 
            ? intl.formatMessage({ id: 'BULK_UPLOAD.MODAL.TITLE.COMPLETED' })
            : intl.formatMessage({ id: 'BULK_UPLOAD.MODAL.TITLE.UPLOADING' })
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex flex-column">
          {/* Overall Progress */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">
                {intl.formatMessage({ id: 'BULK_UPLOAD.PROGRESS.OVERALL' })}
              </span>
              <span className="badge badge-primary">{progress.percentage}%</span>
            </div>
            <div className="progress mb-2" style={{ height: '10px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${progress.percentage}%` }}
                aria-valuenow={progress.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
            <small className="text-muted">
              {intl.formatMessage(
                { id: 'BULK_UPLOAD.PROGRESS.ITEMS_PROCESSED' },
                { 
                  processed: progress.processedItems,
                  total: progress.totalItems
                }
              )}
            </small>
          </div>

          {/* Success Message */}
          {progress.isCompleted && dataResults?.dataError && dataResults?.dataError.length === 0 && (
            <div className="alert alert-success d-flex align-items-center">
              <i className="fas fa-check-circle me-2"></i>
              <div>
                <strong>
                  {intl.formatMessage({ id: 'BULK_UPLOAD.SUCCESS.TITLE' })}
                </strong>
                <br />
                {intl.formatMessage(
                  { id: 'BULK_UPLOAD.SUCCESS.MESSAGE' },
                  { total: progress.totalItems }
                )}
              </div>
            </div>
          )}

          {/* Partial Success Message */}
          {progress.isCompleted && dataResults?.dataError && dataResults?.dataError.length > 0 && (
            <div className="alert alert-warning d-flex align-items-center">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <div>
                <strong>
                  {intl.formatMessage({ id: 'BULK_UPLOAD.PARTIAL_SUCCESS.TITLE' })}
                </strong>
                <br />
                {intl.formatMessage(
                  { id: 'BULK_UPLOAD.PARTIAL_SUCCESS.MESSAGE' },
                  { 
                    success: dataResults?.dataSuccess.length,
                    failed: dataResults?.dataError.length
                  }
                )}
              </div>
            </div>
          )}

          {/* Loading Animation */}
          {!progress.isCompleted && (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2 text-muted">
                {intl.formatMessage({ id: 'BULK_UPLOAD.LOADING.MESSAGE' })}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      {progress.isCompleted && (
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onClose()
              setDataResults(null)
            }}
          >
            <i className="fas fa-check me-2"></i>
            {intl.formatMessage({ id: 'BULK_UPLOAD.ACTION.CLOSE' })}
          </button>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default BulkUploadProgressModal 